import { useCallback, useMemo, useRef, type Dispatch, type SetStateAction } from "react";
import {
  calculateOneSubBasinDetail,
  calculateProjectAsync,
  type SubBasinTruncation,
  type SubBasinResult,
} from "../engine/engine.ts";
import { translateError, type Translate } from "../i18n/hooks.ts";
import type { CalculationOptions } from "../model/calculationOptions.ts";
import {
  dateRangeIndices,
  trimSeriesByIndexRange,
  type DailySeries,
  type ProjectSeries,
} from "../model/dailySeries.ts";
import type { EvalHidProject } from "../model/types.ts";
import { validateProject } from "../model/validations.ts";
import type { State } from "./appState.ts";

/**
 * Builds the "N sub-basins excluded/truncated for invalid/empty data"
 * notice text (`findTruncatedSubBasins`) — `null` when nothing was
 * affected. Two separate sentences: sub-basins with NO valid data at all in
 * the calculated range (`validSteps === 0`, no result whatsoever) read
 * differently from ones truncated partway through (a real, partial result
 * — see engine.ts's own `firstInvalidIndex`/`padToFullLength`). Pulled out
 * as its own function (rather than inline in `runCalculation`) for the same
 * reason as the `with*` state helpers above: directly unit-testable
 * without a React environment.
 */
export function invalidDataWarningMessage(
  t: Translate,
  entries: SubBasinTruncation[]
): string | null {
  if (entries.length === 0) return null;
  const excluded = entries.filter((e) => e.validSteps === 0);
  const truncated = entries.filter((e) => e.validSteps > 0);
  const parts: string[] = [];
  if (excluded.length > 0) {
    const list = excluded.map((e) => e.subBasin.description).join(", ");
    parts.push(t("app.calculationExcludedWarning", { n: excluded.length, list }));
  }
  if (truncated.length > 0) {
    const list = truncated
      .map(
        (e) => `${e.subBasin.description} (${t("app.truncatedFromDate", { date: e.gapStartDate })})`
      )
      .join(", ");
    parts.push(t("app.calculationTruncatedWarning", { n: truncated.length, list }));
  }
  return parts.join(" ");
}

/**
 * "Calculate" always opens the options dialog (dates/aquifers) —
 * `runCalculation` (in the hook below) is what actually runs the
 * calculation, called both by the dialog (on confirm) and by the direct
 * recalculate button (with the last saved options, without asking again).
 *
 * Runs via `calculateProjectAsync` (engine.ts) instead of the old
 * synchronous `calculateProject` — a large project used to freeze the tab
 * for the whole run with no feedback; now it yields periodically and
 * reports progress (`calculationProgress`, shown in TopBar.tsx) — same
 * "async + periodic yield" shape the calibration search already used
 * (sceua.ts), just applied to the one-shot main calculation instead of a
 * search loop. The guard/state-transition pieces below are pulled out as
 * named, exported functions (rather than inline `setState` closures) so
 * they're directly unit-testable without a React environment — see
 * project_evalhid_code_audit.md for the ongoing App.tsx breakup this is
 * part of.
 */
export function canRunCalculation(current: State): boolean {
  if (current.phase !== "loaded") return false;
  if (current.calculationProgress) return false; // already running — see runCalculation's own guard.
  const { precipitation, etp } = current.loadedProject.series;
  return Boolean(precipitation && etp);
}

export function withCalculationStarted(current: State, total: number): State {
  return current.phase === "loaded"
    ? {
        ...current,
        calculationDialogOpen: false,
        calculationError: null,
        invalidDataWarning: null,
        calculationProgress: { done: 0, total },
      }
    : current;
}

export function withCalculationProgress(current: State, done: number, total: number): State {
  return current.phase === "loaded"
    ? { ...current, calculationProgress: { done, total } }
    : current;
}

export function withCalculationSucceeded(
  current: State,
  results: SubBasinResult[],
  options: CalculationOptions,
  invalidDataWarning: string | null = null
): State {
  return current.phase === "loaded"
    ? {
        ...current,
        results,
        calculationError: null,
        invalidDataWarning,
        calculationProgress: null,
        resultsPanelOpen: true,
        calculationOptions: options,
      }
    : current;
}

export function withCalculationFailed(current: State, message: string): State {
  return current.phase === "loaded"
    ? { ...current, calculationError: message, calculationProgress: null }
    : current;
}

export function withCalculationDialogOpened(current: State): State {
  return current.phase === "loaded" ? { ...current, calculationDialogOpen: true } : current;
}

export function withCalculationDialogClosed(current: State): State {
  return current.phase === "loaded" ? { ...current, calculationDialogOpen: false } : current;
}

/**
 * Restricts one series to `startDate`/`endDate`, locating the range within
 * ITS OWN `dates` — not a shared index borrowed from another series. Matches
 * `SeriesTemporales.cs`'s real behavior: `Program.cs` opens 4 independent
 * `Arch_datos_temporales` readers (`arch_pr`/`arch_etp`/`arch_tempe`/
 * `arch_bomb`), each running its own `Apertura()` search for `G.fecha_ini`
 * within its own file — temperature/pumping are optional and may plausibly
 * come from a different source/period than precipitation/ETP (e.g. a
 * shorter station record), so assuming they share precipitation's exact
 * row-to-date mapping isn't safe. Falls back to the untrimmed series if the
 * chosen dates somehow aren't found in THIS series (shouldn't happen for
 * precipitation itself — CalculationOptionsDialog.tsx only lets the user
 * pick dates already within precipitation's own loaded range — but a
 * secondary series with a genuinely different calendar could still miss
 * them; used as-is rather than blocking, same leniency as before this fix,
 * just no longer silently misaligned by reusing precipitation's index).
 */
function trimToConfirmedRange(
  series: DailySeries,
  startDate: string,
  endDate: string
): DailySeries {
  const range = dateRangeIndices(series.dates, startDate, endDate);
  return range ? trimSeriesByIndexRange(series, range) : series;
}

/**
 * `pumping` is only ever returned non-null when `options.modelPumping` is
 * checked — matching the real engine exactly: `Program.cs` only resolves/
 * applies Ent_Bomb.csv at all when `Op_bombeos == 1` (the "Modelar bombeos"
 * checkbox), regardless of whether the file/series itself has real data —
 * see calculationOptions.ts's own header for the full trace. Unchecked, the
 * loaded pumping series is simply never passed to the engine, same as if it
 * had never been loaded — not a special case inside the 6 models themselves.
 */
export function seriesForCalculation(
  precipitation: DailySeries,
  etp: DailySeries,
  temperature: DailySeries | null,
  pumping: DailySeries | null,
  options: CalculationOptions
): {
  precipitation: DailySeries;
  etp: DailySeries;
  temperature: DailySeries | null;
  pumping: DailySeries | null;
} {
  const modeledPumping = options.modelPumping ? pumping : null;
  const { startDate, endDate } = options;
  return {
    precipitation: trimToConfirmedRange(precipitation, startDate, endDate),
    etp: trimToConfirmedRange(etp, startDate, endDate),
    temperature: temperature ? trimToConfirmedRange(temperature, startDate, endDate) : null,
    pumping: modeledPumping ? trimToConfirmedRange(modeledPumping, startDate, endDate) : null,
  };
}

/**
 * The FULL step-by-step detail (`SubBasinResult.detail`) of ONE sub-basin,
 * recomputed on demand — `state.results` (from `calculateProjectAsync`)
 * deliberately no longer carries it for every sub-basin (see
 * `SubBasinResult.detail`'s own comment in engine.ts: at Duero scale, 1728
 * sub-basins, retaining it all cost ~3GB). `CalculationResults.tsx`'s "Ver
 * detalle" tab calls this with whichever sub-basin is currently selected
 * instead of reading `.detail` off the bulk result directly.
 *
 * Plain `useMemo`, not a loading-state/effect pair: recalculating ONE
 * sub-basin over even a multi-decade daily range is a millisecond-scale
 * operation (confirmed while designing this — the 1728-subcuenca cost was
 * always about RETAINING every sub-basin's detail at once, never about the
 * CPU cost of computing one), so there's nothing worth showing a spinner
 * for. Recomputes whenever the selected sub-basin, the project, the loaded
 * series, or the confirmed calculation options change — the same 4 inputs
 * `runCalculation` itself feeds into `calculateProjectAsync`.
 */
export function useSubBasinDetail(
  subBasinCode: number | null,
  project: EvalHidProject | null,
  series: ProjectSeries,
  calculationOptions: CalculationOptions | null
): SubBasinResult | null {
  return useMemo(() => {
    if (subBasinCode == null || project == null || calculationOptions == null) return null;
    const { precipitation, etp, temperature, pumping } = series;
    if (!precipitation || !etp) return null;
    const {
      precipitation: runPrecipitation,
      etp: runEtp,
      temperature: runTemperature,
      pumping: runPumping,
    } = seriesForCalculation(precipitation, etp, temperature, pumping, calculationOptions);
    return calculateOneSubBasinDetail(
      project,
      subBasinCode,
      runPrecipitation,
      runEtp,
      runTemperature,
      runPumping
    );
  }, [subBasinCode, project, series, calculationOptions]);
}

export function useCalculation(
  t: Translate,
  state: State,
  setState: Dispatch<SetStateAction<State>>
) {
  // Read inside the callbacks below instead of closing over `state`
  // directly — same `stateRef` pattern useProjectPersistence.ts already
  // uses. Real gap found by deep-review (2026-08-29): `runCalculation`
  // depended on the WHOLE app state, recreating its own identity on every
  // state change anywhere in the app (a panel toggling, a selection
  // changing...), defeating any memoization downstream that relies on it
  // staying referentially stable (e.g. a button prop, an effect dep).
  const stateRef = useRef(state);
  stateRef.current = state;

  const runCalculation = useCallback(
    (options: CalculationOptions) => {
      const state = stateRef.current;
      if (state.phase !== "loaded" || !canRunCalculation(state)) return;
      if (
        validateProject(t, state.loadedProject.project, state.loadedProject.series).blocking
          .length > 0
      ) {
        return;
      }
      const { precipitation, etp, temperature, pumping } = state.loadedProject.series;
      // canRunCalculation already checked precipitation/etp are non-null —
      // re-narrowed here since TS can't see through that helper call.
      if (!precipitation || !etp) return;
      const {
        precipitation: runPrecipitation,
        etp: runEtp,
        temperature: runTemperature,
        pumping: runPumping,
      } = seriesForCalculation(precipitation, etp, temperature, pumping, options);
      const project = state.loadedProject.project;
      const total = project.subBasins.length;
      // Collected via calculateProjectAsync's own onTruncated callback below
      // instead of a separate findTruncatedSubBasins() pre-scan — real gap
      // found by deep-review (2026-08-29): that pre-scan ran the exact same
      // O(sub-basins × timesteps) check calculateOneSubBasin already runs
      // for its own purposes, synchronously on the main thread, a second
      // time, before calculateProjectAsync's first yield even happened.
      // Still can never disagree with what actually got excluded/truncated —
      // it's the SAME per-sub-basin check, not a separate one.
      const invalidDataSubBasins: SubBasinTruncation[] = [];
      setState((current) => withCalculationStarted(current, total));
      calculateProjectAsync(project, runPrecipitation, runEtp, runTemperature, runPumping, {
        onProgress: (done, progressTotal) =>
          setState((current) => withCalculationProgress(current, done, progressTotal)),
        onTruncated: (info) => invalidDataSubBasins.push(info),
      })
        .then((results) => {
          const invalidDataWarning = invalidDataWarningMessage(t, invalidDataSubBasins);
          setState((current) =>
            withCalculationSucceeded(current, results, options, invalidDataWarning)
          );
        })
        .catch((err) => {
          // Before this there was no try/catch: an engine failure mid-run
          // (e.g. an edge case not covered by some model) took down the
          // whole app with no message — see the note on `calculationError`
          // in the State type.
          setState((current) =>
            withCalculationFailed(
              current,
              t("app.calculationError", { message: translateError(err, t) })
            )
          );
        });
    },
    [t, setState]
  );

  const openCalculationDialog = useCallback(() => {
    setState(withCalculationDialogOpened);
  }, [setState]);

  const closeCalculationDialog = useCallback(() => {
    setState(withCalculationDialogClosed);
  }, [setState]);

  // Recalculates with the last confirmed options, without reopening the
  // dialog — only called when the button is enabled (calculationOptions
  // isn't null, see TopBar.tsx), but checked again just in case.
  const recalculate = useCallback(() => {
    const state = stateRef.current;
    if (state.phase === "loaded" && state.calculationOptions) {
      runCalculation(state.calculationOptions);
    }
  }, [runCalculation]);

  return { runCalculation, openCalculationDialog, closeCalculationDialog, recalculate };
}
